// src/pages/interactive-floor-plans/components/FloorPlanCanvas.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../components/AppIcon";
import SvgFloorPlan from "./SvgFloorPlan";
import { supabase } from "../../../lib/supabase";

// =====================================================
// ✅ CONFIG
// =====================================================
const PROJECT_NAME = "Residencial 3 Volcanes";
const TABLE_NAME = "floorplan_lots";

const STAGE_KEYS = {
  I: "ABCDEFGHIJKLMNOPQRS"?.split(""),   // Etapa 1: A..S
  II: "ABCDEFGHIJKLMNOPQRST"?.split(""), // Etapa 2: A..T
};
const STAGES = ["I", "II"];

// =====================================================
// ✅ HELPERS
// =====================================================
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const polygonToPointsAttr = (pts) => pts?.map((p) => `${p?.x},${p?.y}`)?.join(" ");

// ID: Etapa I => A-01 (simple)
// ID: Etapa II => A-II-01
const formatLotId = (block, stage, num) => {
  const nn = String(num)?.padStart(2, "0");
  if (stage === "I") return `${block}-${nn}`;
  return `${block}-${stage}-${nn}`;
};

// Lee lot_code y detecta block/stage/num
const parseLotCode = (lotCode, fallbackBlock = "A") => {
  const s = String(lotCode || "")?.trim()?.toUpperCase();

  // A-II-01
  let m = s?.match(/^([A-Z])\-([IVXLCDM]+)\-(\d+)$/);
  if (m) {
    return { block: m?.[1], stage: m?.[2], num: Number(m?.[3]) };
  }

  // A-01 (etapa I)
  const m2 = s?.match(/^([A-Z])\-(\d+)$/);
  if (m2) {
    return { block: m2?.[1], stage: "I", num: Number(m2?.[2]) };
  }

  return { block: String(fallbackBlock || "A")?.toUpperCase(), stage: "I", num: null };
};

// Próximo número de lote dentro de (stage, block)
const getNextLotNumber = ({ block, stage, arr }) => {
  const b = String(block)?.toUpperCase();
  const st = String(stage)?.toUpperCase();
  let max = 0;

  for (const item of arr || []) {
    const id = String(item?.id || "")?.toUpperCase();

    // Etapa I: acepta A-01 y A-I-01 (por compatibilidad)
    if (st === "I") {
      let m = id?.match(new RegExp(`^${b}-(\\d+)$`));
      if (m?.[1]) max = Math.max(max, Number(m?.[1]));
      m = id?.match(new RegExp(`^${b}-I-(\\d+)$`));
      if (m?.[1]) max = Math.max(max, Number(m?.[1]));
      continue;
    }

    // Etapa II: A-II-01
    let m = id?.match(new RegExp(`^${b}-${st}-(\\d+)$`));
    if (m?.[1]) max = Math.max(max, Number(m?.[1]));
  }

  return max + 1;
};

const screenToSvg = (svgEl, clientX, clientY) => {
  const pt = svgEl?.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svgEl?.getScreenCTM();
  if (!ctm) return null;
  return pt?.matrixTransform(ctm?.inverse());
};

const buildEmptyStages = () => {
  const base = {};
  for (const stage of STAGES) {
    base[stage] = {};
    for (const b of STAGE_KEYS?.[stage]) base[stage][b] = [];
  }
  return base;
};
const getLotStyle = (status) => {
  // available | reserved | sold
  if (status === "reserved") {
    return {
      fill: "rgba(255, 153, 0, 0.84)",   // naranja (separado)
      stroke: "rgba(255, 123, 0, 0.92)",
      strokeWidth: 2,
    };
  }
  if (status === "sold") {
    return {
      fill: "rgba(255, 0, 0, 0.67)",     // rojo (vendido)
      stroke: "rgba(255, 0, 0, 1)",
      strokeWidth: 2,
    };
  }

  // available (default turquesa)
  return {
    fill: "rgba(255, 238, 0, 0.4)",
    stroke: "rgba(0, 255, 255, 0.49)",
    strokeWidth: 2,
  };
};

// =====================================================
// ✅ COMPONENT
// =====================================================
const FloorPlanCanvas = ({ floorPlanData, onLotSelect, selectedLot, filters }) => {
  if (floorPlanData?.type === "svg") {
    return (
      <div className="relative w-full h-full bg-muted/30 rounded-xl overflow-hidden">
        <SvgFloorPlan
          svgUrl={floorPlanData?.svgUrl}
          selectedLotId={selectedLot?.id}
          onLotSelect={onLotSelect}
          filters={filters}
        />
      </div>
    );
  }

  // ✅ Por ahora todos pueden dibujar (luego lo restringes)
  const canEdit = true;

  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [debugPoint, setDebugPoint] = useState(null);
  const [debugText, setDebugText] = useState("");

  const [drawMode, setDrawMode] = useState(false);
  const [draftPoints, setDraftPoints] = useState([]);

  // ✅ Etapa + manzana activas
  const [activeStage, setActiveStage] = useState("I");
  const [activeBlock, setActiveBlock] = useState("N");
  const [showOnlyActiveBlock, setShowOnlyActiveBlock] = useState(false);

  const VIEWBOX_W = 3370;
  const VIEWBOX_H = 2384;

  const [blocksByStage, setBlocksByStage] = useState(buildEmptyStages());
  const [dbLoading, setDbLoading] = useState(false);

  // ✅ Crosshair CSS (puntería)
  const crosshairCss = `
    .fp-crosshair, .fp-crosshair * { cursor: crosshair !important; }
  `;

  // =========================================================
  // ✅ CARGAR DESDE BD
  // =========================================================
  useEffect(() => {
    let cancelled = false;

    const loadFromDB = async () => {
      try {
        setDbLoading(true);
        setDebugText("Cargando polígonos desde BD...");

        const { data, error } = await supabase?.from(TABLE_NAME)?.select("project_name, block, lot_code, points, status, area_m2, price_total, discount_enabled, customer_name, deal_amount")?.eq("project_name", PROJECT_NAME)?.order("lot_code", { ascending: true });

        if (error) throw error;

        const base = buildEmptyStages();

        for (const row of data || []) {
          const lotCode = String(row?.lot_code || "");
          const parsed = parseLotCode(lotCode, row?.block);

          const stage = STAGES?.includes(String(row?.stage || parsed?.stage)?.toUpperCase())
            ? String(row?.stage || parsed?.stage)?.toUpperCase()
            : "I";

          const block = String(parsed?.block || row?.block || "")?.toUpperCase();

          if (!base?.[stage]?.[block]) continue;

          const pts = Array.isArray(row?.points) ? row?.points : [];
          base?.[stage]?.[block]?.push({
            id: row.lot_code,
            points: pts.map((p) => ({ x: Number(p.x), y: Number(p.y) })),
            status: row.status || "available",
            area_m2: row.area_m2 ?? null,
            price_total: row.price_total ?? null,
            discount_enabled: !!row.discount_enabled,
            customer_name: row.customer_name ?? "",
            deal_amount: row.deal_amount ?? null,
          });
        }

        if (!cancelled) {
          setBlocksByStage(base);
          setDebugText(`✅ Polígonos cargados: ${data?.length || 0}`);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setDebugText(`❌ Error BD: ${e?.message || String(e)}`);
      } finally {
        if (!cancelled) setDbLoading(false);
      }
    };

    loadFromDB();
    return () => {
      cancelled = true;
    };
  }, []);

  // =========================================================
  // ✅ GUARDAR EN BD (upsert)
  // =========================================================
  const savePolygonToDB = async ({ block, polygon }) => {
  const payload = {
    project_name: PROJECT_NAME,
    block,
    lot_code: polygon.id,          // acá ya viene A-II-01, etc.
    points: polygon.points,
    status: polygon.status || "available",
    customer_name: polygon.customer_name ?? null,
    deal_amount: polygon.deal_amount ?? null,
    area_m2: polygon.area_m2 ?? null,
    price_total: polygon.price_total ?? null,
    discount_enabled: !!polygon.discount_enabled,
  };

  const { error } = await supabase
    .from(TABLE_NAME)
    .upsert(payload, { onConflict: "project_name,lot_code" });

  if (error) throw error;
};


  // =========================================================
  // ✅ ZOOM / PAN (móvil friendly)
  // =========================================================
  const handleZoomIn = () => setScale((prev) => clamp(prev + 0.2, 0.5, 8));
  const handleZoomOut = () => setScale((prev) => clamp(prev - 0.2, 0.5, 8));
  const handleResetView = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (drawMode) return;
    if (e?.target?.closest?.('[data-lote="1"]')) return;
    if (e?.button === 0) {
      setIsDragging(true);
      setDragStart({
        x: e?.clientX - position?.x,
        y: e?.clientY - position?.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e?.clientX - dragStart?.x,
      y: e?.clientY - dragStart?.y,
    });
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Si cambias etapa y la manzana no existe, corrige
  useEffect(() => {
    const keys = STAGE_KEYS?.[activeStage] || STAGE_KEYS?.I;
    if (!keys?.includes(activeBlock)) {
      setActiveBlock(keys?.[0] || "A");
    }
    setDraftPoints([]);
    setDebugText("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage]);

  // =========================================================
  // ✅ HOTKEYS
  // =========================================================
  useEffect(() => {
    const onKey = async (e) => {
      if (e?.key === "Backspace") e?.preventDefault();

      if (e?.key === "d" || e?.key === "D") {
        if (!canEdit) return;
        setDrawMode((p) => !p);
        return;
      }
      if (e?.key === "c" || e?.key === "C") {
        if (!canEdit) return;
        setDraftPoints([]);
        setDebugText("Draft limpiado");
        return;
      }
      if (e?.key === "Backspace") {
        if (!canEdit) return;
        setDraftPoints((p) => p?.slice(0, -1));
        return;
      }

      // [ ] cambiar manzana dentro de la etapa
      if (e?.key === "[") {
        const keys = STAGE_KEYS?.[activeStage] || STAGE_KEYS?.I;
        setActiveBlock((cur) => {
          const idx = keys?.indexOf(cur);
          return idx <= 0 ? keys?.[keys?.length - 1] : keys?.[idx - 1];
        });
        return;
      }
      if (e?.key === "]") {
        const keys = STAGE_KEYS?.[activeStage] || STAGE_KEYS?.I;
        setActiveBlock((cur) => {
          const idx = keys?.indexOf(cur);
          return idx === -1 || idx >= keys?.length - 1 ? keys?.[0] : keys?.[idx + 1];
        });
        return;
      }

      if (e?.key === "Enter") {
        if (!canEdit) return;

        if (draftPoints?.length < 3) {
          setDebugText("⚠️ Agrega mínimo 3 puntos antes de Enter");
          return;
        }

        const stage = activeStage;
        const block = activeBlock;

        const curr = blocksByStage?.[stage]?.[block] || [];
        const nextNum = getNextLotNumber({ block, stage, arr: curr });
        const nextId = formatLotId(block, stage, nextNum);

        const newPoly = {
          id: nextId,
          points: draftPoints?.map((p) => ({ x: Math.round(p?.x), y: Math.round(p?.y) })),
          status: "available",
          customer_name: "",
          deal_amount: null,
        };

        try {
          setDebugText(`Guardando ${nextId} en BD...`);
          await savePolygonToDB({ block, stage, polygon: newPoly });

          setBlocksByStage((prev) => ({
            ...prev,
            [stage]: {
              ...(prev?.[stage] || {}),
              [block]: [...(prev?.[stage]?.[block] || []), { ...newPoly, stage, block }],
            },
          }));

          setDraftPoints([]);
          setDebugText(`✅ Guardado en BD: ${nextId}`);
        } catch (err) {
          console.error(err);
          setDebugText(`❌ Error guardando BD: ${err?.message || String(err)}`);
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, draftPoints, activeStage, activeBlock, blocksByStage]);

  const uiHint = useMemo(() => {
    if (!drawMode) return "Modo normal | D = dibujar | [ ] = cambiar manzana";
    return "MODO DIBUJO: crosshair activo | click=agrega punto | Enter=guardar en BD | Backspace=undo | C=limpiar | D=salir";
  }, [drawMode]);

  const polygonsToRender = useMemo(() => {
    if (!blocksByStage) return [];

    if (showOnlyActiveBlock) {
      return (blocksByStage?.[activeStage]?.[activeBlock] || [])?.map((p) => ({
        ...p,
        __stage: activeStage,
        __block: activeBlock,
      }));
    }

    const out = [];
    for (const stage of STAGES) {
      const keys = STAGE_KEYS?.[stage] || [];
      for (const b of keys) {
        const arr = blocksByStage?.[stage]?.[b] || [];
        for (const p of arr) out?.push({ ...p, __stage: stage, __block: b });
      }
    }
    return out;
  }, [blocksByStage, showOnlyActiveBlock, activeStage, activeBlock]);

  // =========================================================
  // ✅ UI safe areas (móvil)
  // =========================================================
  const topPad = "calc(env(safe-area-inset-top, 0px) + 12px)";
  const leftPad = "calc(env(safe-area-inset-left, 0px) + 12px)";
  const rightPad = "calc(env(safe-area-inset-right, 0px) + 12px)";

  return (
    <div className={`relative w-full h-full bg-muted/30 rounded-xl overflow-hidden ${drawMode ? "fp-crosshair" : ""}`}>
      {drawMode && <style>{crosshairCss}</style>}
      {/* Zoom Controls */}
      <div className="absolute z-50 flex flex-col gap-2" style={{ top: topPad, right: rightPad }}>
        <button
          onClick={handleZoomIn}
          className="w-10 h-10 sm:w-12 sm:h-12 bg-card rounded-xl shadow-md flex items-center justify-center hover:bg-muted transition-smooth touch-target focus-ring"
          title="Acercar"
        >
          <Icon name="ZoomIn" size={20} />
        </button>
        <button
          onClick={handleZoomOut}
          className="w-10 h-10 sm:w-12 sm:h-12 bg-card rounded-xl shadow-md flex items-center justify-center hover:bg-muted transition-smooth touch-target focus-ring"
          title="Alejar"
        >
          <Icon name="ZoomOut" size={20} />
        </button>
        <button
          onClick={handleResetView}
          className="w-10 h-10 sm:w-12 sm:h-12 bg-card rounded-xl shadow-md flex items-center justify-center hover:bg-muted transition-smooth touch-target focus-ring"
          title="Restablecer vista"
        >
          <Icon name="Maximize2" size={20} />
        </button>
      </div>
      {/* Panel */}
      <div
        className="absolute z-50 bg-card px-3 sm:px-4 py-2 rounded-xl shadow-md space-y-2"
        style={{
          top: topPad,
          left: leftPad,
          maxWidth: "min(520px, calc(100vw - 120px))",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-medium">{Math.round(scale * 100)}%</div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-muted-foreground">Etapa</span>
              <select
                value={activeStage}
                onChange={(e) => setActiveStage(e?.target?.value)}
                className="text-[12px] border rounded-md px-2 py-1 bg-background"
                title="Etapa"
              >
                {STAGES?.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-1">
              <span className="text-[11px] text-muted-foreground">Manzana</span>
              <select
                value={activeBlock}
                onChange={(e) => setActiveBlock(e?.target?.value)}
                className="text-[12px] border rounded-md px-2 py-1 bg-background"
                title="Manzana"
              >
                {(STAGE_KEYS?.[activeStage] || STAGE_KEYS?.I)?.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-1 text-[11px] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOnlyActiveBlock}
                onChange={(e) => setShowOnlyActiveBlock(e?.target?.checked)}
              />
              solo activa
            </label>
          </div>
        </div>

        <div className="text-[11px] text-muted-foreground">
          {dbLoading ? "⏳ Cargando BD..." : debugText || "Coords: (haz click)"}
        </div>

        <div className={`text-[11px] ${drawMode ? "text-primary" : "text-muted-foreground"}`}>{uiHint}</div>

        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              className={`text-[12px] px-2 py-1 rounded-md border ${
                drawMode ? "bg-primary text-white" : "bg-background"
              }`}
              onClick={() => setDrawMode((p) => !p)}
              title="Toggle modo dibujo (D)"
            >
              {drawMode ? "Dibujo: ON" : "Dibujo: OFF"}
            </button>

            <button
              className="text-[12px] px-2 py-1 rounded-md border bg-background"
              onClick={() => {
                setDraftPoints([]);
                setDebugText("Draft limpiado");
              }}
              title="Limpiar puntos (C)"
            >
              Limpiar
            </button>

            <div className="text-[11px] text-muted-foreground">
              Próximo: <b>{formatLotId(activeBlock, activeStage, 1)}</b> (auto)
            </div>
          </div>
        )}
      </div>
      {/* Canvas */}
      <div
        ref={containerRef}
        className={`w-full h-full ${drawMode ? "cursor-crosshair" : "cursor-move"}`}
        onMouseDown={handleMouseDown}
      >
        <div
          ref={canvasRef}
          style={{
            transform: `translate(${position?.x}px, ${position?.y}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.2s ease-out",
          }}
          className="w-full h-full flex items-center justify-center p-4 sm:p-8 md:p-12 lg:p-16"
        >
          <div className="relative">
            <img
              src={floorPlanData?.imageUrl}
              alt={floorPlanData?.imageAlt}
              className="max-w-full h-auto rounded-lg shadow-lg"
              style={{ pointerEvents: "none" }}
            />

            <svg
              className="absolute inset-0 w-full h-full"
              style={{ zIndex: 40, pointerEvents: "auto", cursor: drawMode ? "crosshair" : "default" }}
              viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
              preserveAspectRatio="xMidYMid meet"
              onClick={(e) => {
                const svgEl = e?.currentTarget;
                const loc = screenToSvg(svgEl, e?.clientX, e?.clientY);
                if (!loc) return;

                const x = Math.round(loc?.x);
                const y = Math.round(loc?.y);

                if (canEdit && drawMode) {
                  e?.stopPropagation();
                  setDraftPoints((p) => [...p, { x, y }]);
                  setDebugText(
                    `DIBUJO ${activeBlock} / Etapa ${activeStage}: punto ${draftPoints?.length + 1} => ${x},${y}`
                  );
                  return;
                }

                setDebugPoint({ x, y });
                setDebugText(`Coords: ${x}, ${y}`);
              }}
            >
              {debugPoint && (
                <g pointerEvents="none">
                  <circle cx={debugPoint?.x} cy={debugPoint?.y} r="10" fill="rgba(0,0,0,0.5)" />
                  <circle cx={debugPoint?.x} cy={debugPoint?.y} r="3" fill="rgba(0,255,255,0.9)" />
                </g>
              )}

              {polygonsToRender.map((lot) => {
  const style = getLotStyle(lot.status);

  return (
    <polygon
      key={lot.id}
      data-lote="1"
      points={polygonToPointsAttr(lot.points)}
      fill={style.fill}
      stroke={style.stroke}
      strokeWidth={style.strokeWidth}
      pointerEvents="all"
      onMouseDown={(ev) => ev.stopPropagation()}
      onPointerDown={(ev) => ev.stopPropagation()}
      onClick={(ev) => {
        ev.stopPropagation();
        onLotSelect?.({
          id: lot.id,
          number: lot.id,
          status: lot.status || "available",
          customer_name: lot.customer_name || "",
          deal_amount: lot.deal_amount ?? null,
          block: lot.__block || activeBlock,
        });
      }}
    />
  );
})}


              {canEdit && drawMode && draftPoints?.length >= 2 && (
                <polyline
                  points={polygonToPointsAttr(draftPoints)}
                  fill="none"
                  stroke="rgba(255,0,0,0.95)"
                  strokeWidth="1"
                  pointerEvents="none"
                />
              )}

              {canEdit &&
                drawMode &&
                draftPoints?.map((p, idx) => (
                  <circle
                    key={`${p?.x}-${p?.y}-${idx}`}
                    cx={p?.x}
                    cy={p?.y}
                    r="3"
                    fill="rgba(255,0,0,0.85)"
                    stroke="rgba(0,0,0,0.3)"
                    strokeWidth="2"
                    pointerEvents="none"
                  />
                ))}
            </svg>

            <svg
              className="absolute inset-0 w-full h-full"
              viewBox="0 0 1000 800"
              preserveAspectRatio="xMidYMid meet"
              style={{ pointerEvents: "none", zIndex: 10 }}
            />
          </div>
        </div>
      </div>
      {/* Instructions */}
      <div
        className="absolute z-50 bg-card px-4 py-2 rounded-xl shadow-md"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <p className="text-xs md:text-sm text-muted-foreground text-center">
          Arrastra para mover • Usa zoom • D = dibujar • Enter = guardar en BD • [ ] = cambiar manzana
        </p>
      </div>
    </div>
  );
};

export default FloorPlanCanvas;
